import axios from "axios"
import { t } from "i18next"
import { Notification } from "modules/shared/components/Auth/Notification"
import config from "./config"

export const TOKEN_KEY = "token"

export function setUpAxios() {
  axios.defaults.baseURL = config.apiEndpoint

  axios.interceptors.request.use((config) => {
    if (!config.url?.startsWith("/api/v1/")) {
      config.withCredentials = true
    }

    if (config.data && dataHasFile(config.data)) {
      const formData = new FormData()

      appendFormData(formData, config.data)

      config.headers["Content-Type"] = "multipart/form-data"
      config.data = formData
    }

    const token = localStorage.getItem(TOKEN_KEY)
    if (token) config.headers.Authorization = `Bearer ${token}`

    return config
  })

  axios.interceptors.response.use(
    (response) => {
      // Check if response data contains _newCompanyId (workspace auto-switch)
      const data = response.data?.data
      if (data && typeof data === "object" && "_newCompanyId" in data && data._newCompanyId) {
        const queryClient = window.queryClient
        const newCompanyId = String(data._newCompanyId)

        // Update auth cache immediately to avoid glitchy UI
        queryClient.setQueryData(["auth"], (oldData: unknown) => {
          if (oldData && typeof oldData === "object" && "companyId" in oldData) {
            return {
              ...oldData,
              companyId: newCompanyId,
            }
          }
          return oldData
        })

        // Invalidate ALL queries when switching companies
        queryClient.invalidateQueries()

        Notification({
          title: t("auth.autoSwitchNotification.title"),
          description: t("auth.autoSwitchNotification.description"),
        })
      }

      return response
    },
    (error) => {
      return Promise.reject(error)
    },
  )
}

function dataHasFile(data: Record<string, unknown>) {
  return Object.values(data).some((value) => value instanceof FileList)
}

// Recursively append fields to FormData, supporting nested objects/arrays
function appendFormData(formData: FormData, data: Record<string, unknown>, parentKey?: string) {
  for (const key in data) {
    if (!data.hasOwnProperty(key) || data[key] === undefined || data[key] === null) continue
    const value = data[key]
    const formKey = parentKey ? `${parentKey}[${key}]` : key

    if (value instanceof FileList && value[0]) {
      // Only support one file for now
      formData.append(formKey, value[0])
    } else if (value instanceof File) {
      formData.append(formKey, value)
    } else if (Array.isArray(value)) {
      value.forEach((item, idx) => {
        // Use bracket notation for arrays: key[0], key[1], ...
        appendFormData(formData, { [idx]: item }, formKey)
      })
    } else if (typeof value === "object") {
      appendFormData(formData, value as Record<string, unknown>, formKey)
    } else {
      formData.append(formKey, value.toString())
    }
  }
}
